import React, { useEffect, useState } from "react";
import { Image as ImageIcon, Video, Link2, Upload, Trash2, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const MediaManager = () => {
  const [items, setItems] = useState([]);
  const [link, setLink] = useState({ title: "", media_type: "image", url: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/landing-media");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const addLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/landing-media/link", link);
      toast.success("Media ditambahkan");
      setLink({ title: "", media_type: "image", url: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah media");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      await api.post("/landing-media/upload", fd);
      toast.success("Media terunggah");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah media");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Hapus media ${m.title || ""}?`)) return;
    await api.delete(`/landing-media/${m.id}`);
    toast.success("Media dihapus");
    load();
  };

  return (
    <div className="space-y-6" data-testid="media-manager">
      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-3">
          <ImageIcon className="w-5 h-5 text-blue-400" />
          <h3 className="font-display text-xl font-semibold">Galeri Halaman Awal</h3>
        </div>
        <p className="mt-2 text-sm text-slate-400">
          Foto dan video di bawah tampilan tracking. Bisa unggah file atau tempel link.
        </p>

        <div className="mt-7 grid gap-6 lg:grid-cols-2">
          <form onSubmit={addLink} className="rounded-xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
              <Link2 className="w-4 h-4 text-blue-400" /> Tambah via Link
            </div>
            <div className="mt-4 space-y-3">
              <Input
                data-testid="media-title-input"
                placeholder="Judul (opsional)"
                value={link.title}
                onChange={(e) => setLink({ ...link, title: e.target.value })}
                className="h-11 bg-slate-900/70 border-white/10"
              />
              <Input
                data-testid="media-url-input"
                placeholder="https://... (gambar atau video)"
                value={link.url}
                onChange={(e) => setLink({ ...link, url: e.target.value })}
                className="h-11 bg-slate-900/70 border-white/10"
              />
              <select
                data-testid="media-type-select"
                value={link.media_type}
                onChange={(e) => setLink({ ...link, media_type: e.target.value })}
                className="h-11 w-full rounded-xl bg-slate-900/70 border border-white/10 px-4 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="image">Gambar</option>
                <option value="video">Video</option>
              </select>
              <Button
                type="submit"
                data-testid="add-media-link-btn"
                disabled={busy}
                className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-500 font-semibold"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tambah Media"}
              </Button>
            </div>
          </form>

          <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
              <Upload className="w-4 h-4 text-blue-400" /> Unggah File
            </div>
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">
                Foto (JPG/PNG/WEBP) atau Video (MP4/WEBM/MOV) · maks 60MB
              </Label>
              <input
                data-testid="media-upload-input"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.mp4,.webm,.mov"
                onChange={(e) => upload(e.target.files?.[0])}
                className="mt-3 h-11 w-full rounded-xl bg-slate-900/70 border border-white/10 px-3 py-2 text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:text-xs file:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6 md:p-8">
        <h3 className="font-display text-xl font-semibold">
          Media Terpasang <span className="text-slate-500 text-sm font-normal">({items.length})</span>
        </h3>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-list">
          {items.length === 0 && <p className="text-sm text-slate-500">Belum ada media.</p>}
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`media-item-${m.id}`}
              className="rounded-xl border border-white/10 bg-slate-900/60 overflow-hidden lift"
            >
              <div className="aspect-video bg-slate-950 flex items-center justify-center">
                {m.media_type === "video" ? (
                  <Video className="w-8 h-8 text-blue-400" />
                ) : (
                  <img src={mediaUrl(m)} alt={m.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-slate-200 truncate">{m.title || "(tanpa judul)"}</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">
                    {m.media_type} · {m.source}
                  </div>
                </div>
                <Button
                  data-testid={`delete-media-btn-${m.id}`}
                  onClick={() => remove(m)}
                  variant="outline"
                  className="h-9 w-9 p-0 rounded-lg border-red-400/20 bg-red-500/10 text-red-300 hover:bg-red-500/20 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
